import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {requestConfig} from '../../shared/constants/request.config'
import {
  FacilityType,
  FacilityTypeName,
  FIRE_PARTITION_POSITIONS,
  FIRE_PARTITION_REQUIREMENTS,
  FirePartition,
  FirePartitionStatusName,
  FirePartitionTypeName,
  getFirePartitionPositionName,
  getFirePartitionRequirementsName,
  InstallationMethodName,
  Material,
  MaterialSpecification,
  MaterialSpecificationType,
  MaterialStatusName,
  MaterialTypesModel,
} from "../models/firepartition.type";

const API = '/api/webapp'

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private _http: HttpClient) {
  }

  /**regio
   * @param  {string} key          the key for the request configuration object
   * @param  {any} payLoad         pay-load to be sent in the request
   * @param  {string} pathParam    path parameter if present
   * @returns Observable
   */
  public request(key: string, payLoad: any = '', pathParam: string = ''): Observable<any> {
    const uri = `${API}${requestConfig[key].path}`;

    // creating an header with the content-type
    let headers = new HttpHeaders;
    if (requestConfig[key].contentType != undefined) {
      headers = headers.append('content-type', requestConfig[key].contentType);
    }
    switch (requestConfig[key].method) {
      case 'get': {
        return this._http.get(uri + pathParam + payLoad, {headers});
      }
      case 'post': {
        return this._http.post(uri + pathParam, payLoad, {headers});
      }
      case 'put': {
        return this._http.put(uri + pathParam, payLoad, {headers});
      }
      case 'patch': {
        return this._http.patch(uri + pathParam, payLoad, {headers});
      }
      case 'delete': {
        return this._http.delete(uri + pathParam + payLoad, {headers});
      }
    }
  }

  public async getMaterialTypes(): Promise<MaterialTypesModel[]> {
    const materialTypes = await firstValueFrom(this._http.get<MaterialTypesModel[]>(`${API}/firepartition/material-types`, CONTENT_JSON));
    return materialTypes.map(convertToMaterialType)
  }

  public async getAllSeals(paginationObj) {
    const result: any = await firstValueFrom(this._http.post<FirePartitionDto[]>(`${API}/firepartition/list`, paginationObj, CONTENT_JSON));
    result.firepartitions = result.firepartitions.map(convertToFirePartition)
    return result
  }

  public async getAllFPListForZone(mapId: string, zoneId: string) {
    const firepartitions = await firstValueFrom(this._http.get<FirePartitionDto[]>(`${API}/map/${mapId}/zone/${zoneId}/firepartitions`, CONTENT_JSON));
    return firepartitions.map(convertToFirePartition)
  }

  public async editFirePartition(firePartition: FirePartitionDto) {
    const updatedFp = await firstValueFrom(this._http.put<FirePartitionDto>(`${API}/firepartition/${firePartition._id}/info`, firePartition, CONTENT_JSON));
    return convertToFirePartition(updatedFp)
  }

  public async addFirePartition(firePartition: FirePartitionDto) {
    const newFp = await firstValueFrom(this._http.post<FirePartitionDto>(`${API}/firepartition`, firePartition, CONTENT_JSON));
    return convertToFirePartition(newFp)
  }

  public async getFirePartitionById(fpId: string) {
    const firePartition = await firstValueFrom(this._http.get<FirePartitionDto>(`${API}/firepartition/${fpId}`, CONTENT_JSON));
    return convertToFirePartition(firePartition)
  }

  public async updateMaterials(fpId: string, payload) {
    const materials = await firstValueFrom(this._http.put<MaterialDto[]>(`${API}/firepartition/${fpId}/material`, payload, CONTENT_JSON));
    return convertToFirePartitionMaterials(materials)
  }

  public async getAllMaterialsListProject(projectId: string, payload) {
    return firstValueFrom(this._http.post<any>(`${API}/project//${projectId}/material/cost-management`, payload, CONTENT_JSON));
  }

  public download(url: string): Observable<Blob> {
    return this._http.get(url, {
      responseType: 'blob'
    })
  }
}

function convertToMaterialType(dto: any): MaterialTypesModel {
  return {...dto}
}

export function convertToFirePartitions(dtos: FirePartitionDto[]): FirePartition[] {
  return dtos.map(convertToFirePartition)
}

function convertToFirePartition(dto: any): FirePartition {
  return {
    ...dto,
    position: dto.position ? FIRE_PARTITION_POSITIONS[getFirePartitionPositionName(dto.position)] : null,
    requirements: dto.requirements ? FIRE_PARTITION_REQUIREMENTS[getFirePartitionRequirementsName(dto.requirements)] : null,
    materials: convertToFirePartitionMaterials(dto.materials),
  }
}

function convertToFirePartitionMaterials(dtos: any): Material[] {
  return dtos.map((dto) => ({
    ...dto,
  }))
}

export interface FirePartitionDto {
  _id?: string;
  code: string;
  type: FirePartitionTypeName;
  status: FirePartitionStatusName;
  isExtraWork: boolean;
  qrCode: string;
  floor: string;
  roomNumber: string;
  extra: string;
  positionId: string;
  positionOtherText: string;
  materials: MaterialDto[];
  requirementsId: string;
  facilityTypeName: FacilityTypeName | "";
  brand?: string;
  model?: string;
  identification?: string;
  installationMethod?: InstallationMethodName | "";
}

interface MaterialDto {
  _id: string;
  materialType: {
    _id: string;
    name: string;
    nameDutch: string;
    specificationType: MaterialSpecificationType;
    specifications: MaterialSpecification[],
    forFacilityTypes: FacilityType[] | null;
  },
  materialSpecification: MaterialSpecification | null;
  materialSpecificationText: string | null;
  materialSpecificationBrand: string | null;
  materialRemarks: string | null;
  noOfUnits: number;
  materialStatus: MaterialStatusName;
  materialPlacedDate: string;
  materialMaintenanceDate: string;
  materialMaintenanceIntervalMonths: number;
  pricePerUnit: number;
  createdAt: string;
  updatedAt: string;
}

const CONTENT_JSON = {headers: new HttpHeaders().append('content-type', 'application/json')}
